import React, { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useReactiveVar } from '@apollo/client';
import { IDocumentViewerModalProps, ViewerType } from './document-viewer-modal.types';
import { getViewerType } from './document-viewer.utils';
import { PdfViewer } from './pdf-viewer.component';
import { ImageViewer } from './image-viewer.component';
import { TextViewer } from './text-viewer.component';
import { OfficeViewer } from './office-viewer.component';
import { useGetAssetViewUrl } from '../../domains/assets/asset.service';
import { BASE_API_URL } from '../../../lib/utils';
import { tokenVar } from '../../../providers/token.service';

export const DocumentViewerModal: React.FC<IDocumentViewerModalProps> = ({
  open,
  onClose,
  asset,
  requestToken,
}) => {
  const Authorization = useReactiveVar(tokenVar);

  const viewerType = getViewerType(asset.file);
  const { viewUrl, loading, error, fetchViewUrl } = useGetAssetViewUrl(asset._id, requestToken);

  // Fetch signed URL when modal opens
  useEffect(() => {
    if (open && asset._id) {
      fetchViewUrl();
    }
  }, [open, asset._id, fetchViewUrl]);

  const renderViewer = () => {
    // Show loading state
    if (loading || !viewUrl) {
      return (
        <div className="flex items-center justify-center p-12 text-center">
          <div className="text-gray-500">Loading preview...</div>
        </div>
      );
    }

    // Show error state
    if (error) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="text-red-500 mb-4">
            Failed to load preview. Please try downloading the file instead.
          </div>
        </div>
      );
    }

    // Render appropriate viewer
    // For text and PDF files, use API content endpoint instead of signed URL (to avoid CORS issues)
    const contentUrl = `${BASE_API_URL}/api/asset/${asset._id}/content${requestToken ? `?requestToken=${requestToken}` : ''}`;

    switch (viewerType) {
      case ViewerType.PDF:
        return <PdfViewer url={contentUrl} authorization={Authorization || undefined} requestToken={requestToken} />;
      case ViewerType.IMAGE:
        return <ImageViewer url={viewUrl} alt={asset.name} />;
      case ViewerType.TEXT:
        return <TextViewer url={contentUrl} fileName={asset.name} />;
      case ViewerType.OFFICE:
        return <OfficeViewer url={viewUrl} fileName={asset.name} />;
      case ViewerType.UNSUPPORTED:
      default:
        return (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="text-gray-500 mb-4">
              This file type cannot be previewed in the browser.
            </div>
          </div>
        );
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 truncate pr-4">
                    {asset.name}
                  </Dialog.Title>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={onClose}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Close"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Viewer Content */}
                <div className="bg-gray-50">
                  {renderViewer()}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
